import type { Ref } from 'vue'
import type { DocumentPurposeEnum } from '@/api/valuationV3'
import type { DocumentPurpose } from '@/types/document'
import { createInjectionState } from '@vueuse/core'

import { computed, ref, watch } from 'vue'
import enums from '@/plugins/enums'
import store, { Actions } from '@/store'

export interface Document {
  document_ref: string
  purpose: string
  filename: string
  content_type: string
  dataURL?: string
  // Per-document ability flags returned by the v3 expert list (DocumentActions schema).
  // Absent on v2 documents, asset documents and pending-upload placeholders.
  can?: { delete: boolean, validate: boolean }
  [key: string]: any
}

interface DataLayerOptions {
  // Read-only: the store watches this and copies into localStore. Accepts a plain Ref (legacy)
  // or a ComputedRef (derived server ⊕ pending-upload list from useAssetDocuments).
  data: Ref<Document[] | undefined> | Readonly<Ref<Document[] | undefined>>
  createDocument: (params: {
    purpose: DocumentPurposeEnum
    location?: string
    room_id?: string
    files: File[]
  }) => Promise<any>
  updateDocument?: (document_ref: string, data: Partial<Document>) => Promise<unknown>
  deleteDocument: (document_ref: string) => Promise<unknown>
  getDocumentFileUrl?: (
    document_ref: string,
    options?: { view?: boolean, variant?: 'original' | 'thumbnail' },
  ) => string
  isLoading?: Readonly<Ref<boolean>>
  isError?: Readonly<Ref<boolean>>

  error?: Readonly<Ref<any>>
}

// Create the provide/inject pair
const [useProvideDocumentDataStore, useDocumentDataStore] = createInjectionState(
  (options: DataLayerOptions) => {
    const {
      data,
      createDocument,
      updateDocument: updateDocumentFn,
      deleteDocument,
      getDocumentFileUrl,
      isLoading,
      isError,
      error,
    } = options

    // Local reactive store - documents array
    const localStore = ref<Document[]>([])
    const inFlight = ref(0)
    const saveErrorRef = ref<unknown>(null)

    async function withTracking<T>(fn: () => Promise<T>): Promise<T> {
      inFlight.value++
      try {
        const r = await fn()
        saveErrorRef.value = null
        return r
      }
      catch (err) {
        saveErrorRef.value = err
        throw err
      }
      finally {
        inFlight.value--
      }
    }

    // Sync query data to local store
    watch(
      data,
      (newData) => {
        if (newData) {
          localStore.value = [...newData]
        }
      },
      { immediate: true },
    )

    // Public API
    async function updateDocument(ref: string, update: Partial<Document>) {
      if (!updateDocumentFn)
        return
      return withTracking(async () => {
        const index = localStore.value.findIndex(d => d.document_ref === ref)
        if (index < 0)
          return
        const previous = localStore.value[index]
        localStore.value[index] = { ...previous, ...update }
        try {
          await updateDocumentFn(ref, update)
        }
        catch (err) {
          localStore.value[index] = previous
          throw err
        }
      })
    }

    function notifyMutationError(err: unknown, fallbackMessage: string) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail

      store.dispatch(Actions.NOTIFY_USER, {
        message: typeof detail === 'string' ? detail : fallbackMessage,
        type: enums.feedbackType.NEGATIVE,
        classes: `bg-${enums.feedbackType.NEGATIVE}`,
      })
    }

    async function updateDocumentValidation(
      doc: Document,
      updates: Partial<Pick<Document, 'validation_purpose' | 'valid'>>,
    ) {
      const payload: Partial<Document> = {
        ...updates,
        // validation remarks is not used yet by valuers
        validation_remark: doc.validation_remark,
      }

      if (updates.validation_purpose !== undefined) {
        payload.valid = true
      }

      return updateDocument(doc.document_ref, payload)
    }

    return {
      // Get documents by purpose
      getByPurpose(purpose: string): Document[] {
        return localStore.value.filter(doc => doc.purpose === purpose)
      },

      // Get documents by multiple purposes (empty array returns all)
      getByPurposes(purposes: DocumentPurpose[]): Document[] {
        if (purposes.length === 0)
          return localStore.value
        return localStore.value.filter(doc => purposes.includes(doc.purpose as DocumentPurpose))
      },

      // Get all documents
      getAll(): Document[] {
        return localStore.value
      },

      // Add a new document.
      // No optimistic temp entry is kept here: pending uploads (including ones paused while
      // offline) are derived reactively from the mutation cache in useAssetDocuments and flow in
      // through `data`, so the placeholder appears — and survives a reload — without local
      // bookkeeping. createDocument is fire-and-forget (each file persists/resumes on its own).
      async addDocument(params: {
        purpose: DocumentPurposeEnum
        location?: string
        room_id?: string
        files: File[]
      }) {
        return withTracking(() => createDocument(params))
      },

      // Remove a document (async with optimistic revert)
      async removeDocument(ref: string): Promise<void> {
        return withTracking(async () => {
          const idx = localStore.value.findIndex(d => d.document_ref === ref)
          if (idx < 0)
            return
          const removed = localStore.value[idx]
          // Optimistic removal
          localStore.value = localStore.value.filter(d => d.document_ref !== ref)
          try {
            await deleteDocument(ref)
          }
          catch (err) {
            // Revert: re-insert at original index
            localStore.value = [...localStore.value.slice(0, idx), removed, ...localStore.value.slice(idx)]
            throw err
          }
        })
      },

      updateDocument,
      updateDocumentValidation,
      notifyMutationError,

      getDocumentFileUrl: (
        ref: string,
        fileOptions?: { view?: boolean, variant?: 'original' | 'thumbnail' },
      ) => getDocumentFileUrl?.(ref, fileOptions) ?? '',

      state: computed(() => localStore.value),
      isLoading: isLoading || computed(() => false),
      isError: isError || computed(() => false),
      error: error || computed(() => null),
      isSaving: computed(() => inFlight.value > 0),
      saveError: computed(() => saveErrorRef.value),
    }
  },
)

export { useDocumentDataStore, useProvideDocumentDataStore }

/**
 * Returns true when the document permits the delete action.
 * A missing `can` object (pending-upload placeholder, non-expert doc) is treated as allowed.
 */
export function canDelete(doc: Document): boolean {
  return doc.can?.delete !== false
}

/**
 * Returns true when the document permits validation edits (valid, validation_purpose).
 * A missing `can` object is treated as allowed.
 */
export function canValidate(doc: Document): boolean {
  return doc.can?.validate !== false
}
